import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, Link } from 'react-router-dom';
import { startCase } from 'lodash';
import { PageTitle, Button, Table } from '@cordata/buffet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from 'reactstrap';

import useSlice from '~/utils/slice/hook';
import PartialSlicePicker from '~/components/SituationTables/PartialSlicePicker';
import SitReferenceDataForm from '~/components/SituationTables/ReferenceData/Form';
import {
  fetchAvailableTypes,
  fetchRecords,
  fetchRecord,
  deleteRecord,
  reactivateRecord,
} from '~/utils/situation-tables/reference-data-api';

const SCOPE_LABELS = {
  global: 'System',
  organization: 'Organization',
  division: 'Division',
};

const ScopeCell = ({ value }) => SCOPE_LABELS[value] || value;
ScopeCell.propTypes = {
  value: PropTypes.string,
};
ScopeCell.defaultProps = {
  value: '',
};

const StatusCell = ({ value }) => (
  <Badge color={value ? 'secondary' : 'success'}>
    {value ? 'Inactive' : 'Active'}
  </Badge>
);
StatusCell.propTypes = {
  value: PropTypes.string,
};
StatusCell.defaultProps = {
  value: null,
};

const ActionsCell = ({ original, onDisable, onReactivate }) => (
  <div
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
      }
    }}
    role="button"
    tabIndex={0}
  >
    {original.discarded_at ? (
      <Button
        size="sm"
        color="success"
        outline
        onClick={(e) => onReactivate(original.id, e)}
      >
        Reactivate
      </Button>
    ) : (
      <Button
        size="sm"
        color="danger"
        outline
        onClick={(e) => onDisable(original.id, e)}
      >
        Disable
      </Button>
    )}
  </div>
);
ActionsCell.propTypes = {
  original: PropTypes.shape({
    id: PropTypes.string,
    discarded_at: PropTypes.string,
  }).isRequired,
  onDisable: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
};

const SitReferenceDataCollection = () => {
  const { type } = useParams();
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const {
    fetchAll,
    setCurrentSlice,
    currentSlice,
  } = useSlice();

  // Fetch metadata for available types
  useEffect(() => {
    let isMounted = true;
    fetchAvailableTypes()
      .then((response) => {
        if (isMounted && response.data[type]) {
          setMeta(response.data[type]);
        }
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Error fetching metadata:', error);
        }
      });
    return () => { isMounted = false; };
  }, [type]);

  useEffect(() => {
    fetchAll(['organization', 'division']);
    setCurrentSlice('all');
  }, []);

  useEffect(() => {
    if (!meta) return undefined;

    let isMounted = true;
    setLoading(true);

    const params = {
      withDiscarded: true,
      sliceType: currentSlice.type || 'all',
    };

    if (currentSlice.type === 'organization' && currentSlice.id) {
      params.sliceId = currentSlice.id;
    } else if (currentSlice.type === 'division' && currentSlice.id) {
      params.sliceId = currentSlice.id;
    }

    fetchRecords(type, params)
      .then((response) => {
        if (isMounted) {
          setRecords(response.data.data || []);
          setLoading(false);
        }
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Error fetching records:', error);
          setLoading(false);
        }
      });

    return () => { isMounted = false; };
  }, [refreshKey, currentSlice, type, meta]);

  const handleDisable = useCallback((id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to disable this record?')) {
      deleteRecord(type, id)
        .then(() => setRefreshKey((prev) => prev + 1));
    }
  }, [type]);

  const handleReactivate = useCallback((id, e) => {
    e.stopPropagation();
    reactivateRecord(type, id)
      .then(() => setRefreshKey((prev) => prev + 1));
  }, [type]);

  const tableColumns = useMemo(() => {
    if (!meta) return [];

    const nameCol = meta.name_column;
    const labelCol = meta.label_column;

    return [{
      id: 'label',
      Header: 'Label',
      accessor: labelCol,
    }, {
      id: 'name',
      Header: 'Name',
      accessor: nameCol,
    }, {
      id: 'scope',
      Header: 'Scope',
      accessor: (row) => {
        if (!row.division_uid && !row.org_uid) return 'global';
        if (!row.division_uid && row.org_uid) return 'organization';
        return 'division';
      },
      Cell: ScopeCell,
    }, {
      id: 'status',
      Header: 'Status',
      accessor: 'discarded_at',
      Cell: StatusCell,
    }, {
      id: 'actions',
      Header: 'Actions',
      filterable: false,
      sortable: false,
      Cell: (props) => (
        <ActionsCell
          {...props}
          onDisable={handleDisable}
          onReactivate={handleReactivate}
        />
      ),
    }];
  }, [meta, handleDisable, handleReactivate]);

  const defaultFilterMethod = useCallback(
    (filter, row) =>
      String(row[filter.id] || '').toLowerCase().includes(filter.value.toLowerCase()),
    [],
  );

  useEffect(() => {
    if (selectedId === 'add') {
      setFormData({});
      setFormLoading(false);
      return undefined;
    }
    if (!selectedId) return undefined;

    let isMounted = true;
    setFormLoading(true);
    fetchRecord(type, selectedId)
      .then((response) => {
        if (isMounted) {
          setFormData(response.data.data);
          setFormLoading(false);
        }
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Error fetching record:', error);
          setSelectedId(null);
          setFormLoading(false);
        }
      });

    return () => { isMounted = false; };
  }, [selectedId, type]);

  const handleFormSuccess = useCallback(() => {
    setSelectedId(null);
    setFormData(null);
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleFormCancel = useCallback(() => {
    setSelectedId(null);
    setFormData(null);
  }, []);

  const getTdProps = useCallback((state, rowInfo, column) => ({
    onClick: () => {
      if (column.id !== 'actions') {
        setSelectedId(rowInfo.original.id);
      }
    },
  }), []);

  const displayName = startCase(type.replace(/_/g, ' ')).replace(/s$/, '');

  return (
    <>
      <PageTitle
        action={
          <Button
            outline
            onClick={() => setSelectedId('add')}
          >
            <FontAwesomeIcon icon="plus" />
            <span className="d-none d-md-inline ml-2">Add {displayName}</span>
          </Button>
        }
      >
        <Link to="/toolbox/sit-reference-data">
          <FontAwesomeIcon icon="chevron-left" className="mr-2" />
        </Link>
        {startCase(type.replace(/_/g, ' '))}
      </PageTitle>

      <PartialSlicePicker />

      <Table
        data={records}
        columns={tableColumns}
        loading={loading}
        defaultSorted={[{ id: 'label' }]}
        filterable
        defaultFilterMethod={defaultFilterMethod}
        getTdProps={getTdProps}
      />

      {!formLoading && formData && meta && (
        <SitReferenceDataForm
          type={type}
          meta={meta}
          data={formData}
          isOpen={!!selectedId}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </>
  );
};

export default SitReferenceDataCollection;
